import { supabase } from "@/lib/supabaseClient";

/**
 * Compute the current storm season string (e.g. '2025/26').
 * The season runs Sep 1 → Aug 31. On or after September 1 the season
 * increments to the next calendar year pair.
 * @returns {string} Season label, e.g. '2025/26'.
 */
export function getCurrentSeason() {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth(); // 0-based; 8 = September
  if (month >= 8) {
    return `${year}/${String(year + 1).slice(2)}`;
  }
  return `${year - 1}/${String(year).slice(2)}`;
}

/**
 * Fetch all named storms for the current season from `named_storms`.
 * Data comes from Supabase table: named_storms
 * Columns: storm_name, season, date_named, start_date, notes
 * @returns {Promise<Array>} Array of storm rows ordered by date_named.
 */
export async function getCurrentSeasonStorms() {
  const season = getCurrentSeason();
  const { data, error } = await supabase
    .from("named_storms")
    .select("*")
    .eq("season", season)
    .order("date_named", { ascending: true });
  if (error) throw error;
  return data ?? [];
}

/**
 * Compute the season summary for a given season directly from `named_storms`.
 * Table columns: storm_name, season, date_named, start_date, notes
 * @param {string} season - Season label, e.g. '2025/26'.
 * @returns {Promise<Object|null>} Summary object or null if no storms found.
 */
export async function getSeasonSummary(season) {
  const { data, error } = await supabase
    .from("named_storms")
    .select("storm_name, date_named")
    .eq("season", season)
    .order("date_named", { ascending: true });
  if (error) throw error;
  if (!data || data.length === 0) return null;

  return {
    season,
    storm_count: data.length,
    earliest_named: data[0].date_named,
    latest_named: data[data.length - 1].date_named,
    storm_names: data.map((s) => s.storm_name),
  };
}

/**
 * Fetch all season summaries ordered newest first, computed from `named_storms`.
 * Each entry includes storm_names array and monthly breakdown for display purposes.
 * @returns {Promise<Array>} Array of season summary objects.
 */
export async function getAllSeasons() {
  const { data, error } = await supabase
    .from("named_storms")
    .select("season, storm_name, date_named")
    .order("date_named", { ascending: true });
  if (error) throw error;

  const map = {};
  for (const row of data ?? []) {
    if (!map[row.season]) {
      map[row.season] = {
        season: row.season,
        storm_count: 0,
        earliest_named: row.date_named,
        latest_named: row.date_named,
        storm_names: [],
        // monthly_counts[0]=Sep … monthly_counts[11]=Aug
        monthly_counts: Array(12).fill(0),
        // monthly_storm_names[0]=Sep … monthly_storm_names[11]=Aug
        monthly_storm_names: Array.from({ length: 12 }, () => []),
      };
    }
    map[row.season].storm_count += 1;
    map[row.season].latest_named = row.date_named;
    map[row.season].storm_names.push(row.storm_name);

    // Compute storm-season month index (Sep=0, Oct=1, Nov=2, Dec=3, Jan=4, …, Aug=11).
    // Sep→0: 9-9=0, Oct→1: 10-9=1, …, Dec→3: 12-9=3, Jan→4: 1+3=4, …, Aug→11: 8+3=11
    if (row.date_named && row.date_named.length >= 7) {
      const calMonth = parseInt(row.date_named.substring(5, 7), 10);
      const idx = calMonth >= 9 ? calMonth - 9 : calMonth + 3;
      map[row.season].monthly_counts[idx] += 1;
      map[row.season].monthly_storm_names[idx].push(row.storm_name);
    }
  }
  return Object.values(map).sort((a, b) => b.season.localeCompare(a.season));
}

/**
 * Compute monthly storm counts (Sep→Aug) for a given season from `named_storms`.
 * month_num follows the storm-season order: Sep=1 … Aug=12.
 * @param {string} season - Season label, e.g. '2025/26'.
 * @returns {Promise<Array>} Array of { month_num, storm_count, storm_names } objects.
 */
export async function getMonthlyCounts(season) {
  const { data, error } = await supabase
    .from("named_storms")
    .select("storm_name, date_named")
    .eq("season", season)
    .order("date_named", { ascending: true });
  if (error) throw error;

  const map = {};
  for (const row of data ?? []) {
    // Extract calendar month (1-12) from ISO date string "YYYY-MM-DD"
    if (!row.date_named || row.date_named.length < 7) continue;
    const calMonth = parseInt(row.date_named.substring(5, 7), 10);
    // Convert to storm-season month_num: Sep=1 … Dec=4, Jan=5 … Aug=12
    const month_num = calMonth >= 9 ? calMonth - 8 : calMonth + 4;
    if (!map[month_num]) {
      map[month_num] = { month_num, storm_count: 0, storm_names: [] };
    }
    map[month_num].storm_count += 1;
    map[month_num].storm_names.push(row.storm_name);
  }
  return Object.values(map).sort((a, b) => a.month_num - b.month_num);
}

/**
 * Fetch all storms for a given season from `named_storms`.
 * Data comes from Supabase table: named_storms
 * @param {string} season - Season label, e.g. '2025/26'.
 * @returns {Promise<Array>} Array of storm rows ordered by date_named.
 */
export async function getSeasonStorms(season) {
  const { data, error } = await supabase
    .from("named_storms")
    .select("*")
    .eq("season", season)
    .order("date_named", { ascending: true });
  if (error) throw error;
  return data ?? [];
}

/**
 * Fetch all named storms from `named_storms` with id, storm_name, season, date_named.
 * Used for building per-storm accumulation charts.
 * @returns {Promise<Array>} Array of storm rows ordered by date_named.
 */
export async function getAllStormsRaw() {
  const { data, error } = await supabase
    .from("named_storms")
    .select("id, storm_name, season, date_named")
    .order("date_named", { ascending: true });
  if (error) throw error;
  return data ?? [];
}

/**
 * Fetch all named storms with stats columns for the admin panel.
 * @returns {Promise<Array>} Array of storm rows ordered by date_named.
 */
export async function getAllStormsForAdmin() {
  const { data, error } = await supabase
    .from("named_storms")
    .select("id, storm_name, season, date_named, peak_gust, peak_pressure, peak_rainfall")
    .order("date_named", { ascending: false });
  if (error) throw error;
  return data ?? [];
}

/**
 * Default number of days added to start_date when end_date is unknown.
 * Matches the INTERVAL '2 days' fallback used in mv_storm_ranges.
 */
const FALLBACK_END_DATE_OFFSET_DAYS = 2;

/**
 * Fetch storm date ranges from `mv_storm_ranges`.
 * Falls back to `named_storms` (deriving end_date as start_date + 2 days)
 * if the materialized view does not yet exist.
 * @returns {Promise<Array<{id: string, storm_name: string, season: string, start_date: string, end_date: string}>>}
 */
export async function getStormRanges() {
  const { data, error } = await supabase
    .from("mv_storm_ranges")
    .select("id, storm_name, season, start_date, end_date")
    .order("start_date", { ascending: true });

  if (!error) return data ?? [];

  // Fallback: build from named_storms with a derived end_date
  const { data: storms, error: fallbackError } = await supabase
    .from("named_storms")
    .select("id, storm_name, season, start_date, date_named")
    .order("start_date", { ascending: true });

  if (fallbackError) throw fallbackError;

  return (storms ?? []).map((s) => {
    const base = s.start_date || s.date_named;
    const end = new Date(base + "T00:00:00");
    end.setDate(end.getDate() + FALLBACK_END_DATE_OFFSET_DAYS);
    return {
      id: s.id,
      storm_name: s.storm_name,
      season: s.season,
      start_date: base,
      end_date: end.toISOString().split("T")[0],
    };
  });
}

/**
 * Fetch the top N storms by peak gust speed from `named_storms`.
 * The `peak_gust_mph` column is optional; returns an empty array if the column
 * does not exist or no data is available yet.
 * @param {number} [n=10]
 * @returns {Promise<Array>}
 */
export async function fetchTop10Strongest(n = 10) {
  const { data, error } = await supabase
    .from("named_storms")
    .select("storm_name, season, peak_gust_mph")
    .not("peak_gust_mph", "is", null)
    .order("peak_gust_mph", { ascending: false })
    .limit(n);
  if (error) return [];
  return (data ?? []).map((s, i) => ({
    rank: i + 1,
    name: s.storm_name,
    naming_letter: s.storm_name?.[0]?.toUpperCase(),
    season: s.season,
    peak_gust_mph: s.peak_gust_mph,
  }));
}

/**
 * Fetch the top N storms with the lowest central pressure from `named_storms`.
 * The `min_pressure_hpa` column is optional; returns an empty array if the
 * column does not exist or no data is available yet.
 * @param {number} [n=10]
 * @returns {Promise<Array>}
 */
export async function fetchTop10LowestPressure(n = 10) {
  const { data, error } = await supabase
    .from("named_storms")
    .select("storm_name, season, min_pressure_hpa")
    .not("min_pressure_hpa", "is", null)
    .order("min_pressure_hpa", { ascending: true })
    .limit(n);
  if (error) return [];
  return (data ?? []).map((s, i) => ({
    rank: i + 1,
    name: s.storm_name,
    naming_letter: s.storm_name?.[0]?.toUpperCase(),
    season: s.season,
    peak_pressure_hpa: s.min_pressure_hpa,
  }));
}

/**
 * Fetch the top N wettest storms from `named_storms`.
 * The `max_rainfall_mm` column is optional; returns an empty array if the
 * column does not exist or no data is available yet.
 * @param {number} [n=10]
 * @returns {Promise<Array>}
 */
export async function fetchTop10Wettest(n = 10) {
  const { data, error } = await supabase
    .from("named_storms")
    .select("storm_name, season, max_rainfall_mm")
    .not("max_rainfall_mm", "is", null)
    .order("max_rainfall_mm", { ascending: false })
    .limit(n);
  if (error) return [];
  return (data ?? []).map((s, i) => ({
    rank: i + 1,
    name: s.storm_name,
    naming_letter: s.storm_name?.[0]?.toUpperCase(),
    season: s.season,
    rainfall_mm: s.max_rainfall_mm,
  }));
}

/**
 * Fetch the full detail record for a single named storm.
 * Maps the `named_storms` row to the shape expected by `StormDetailPage`.
 * Stat fields (peak_gust_mph, min_pressure_hpa, max_rainfall_mm) are returned
 * as null when not stored in the database.
 * @param {string} name - Storm name, e.g. 'Ashley'.
 * @param {string} season - Season label, e.g. '2025/26'.
 * @returns {Promise<Object|null>}
 */
export async function fetchStormDetail(name, season) {
  const { data, error } = await supabase
    .from("named_storms")
    .select("*")
    .eq("storm_name", name)
    .eq("season", season)
    .maybeSingle();
  if (error) throw error;
  if (!data) return null;
  return {
    name: data.storm_name,
    id: data.id,
    naming_letter: data.storm_name?.[0]?.toUpperCase(),
    season: data.season,
    start_date: data.start_date || data.date_named,
    end_date: data.end_date ?? null,
    date_named: data.date_named,
    notes: data.notes ?? null,
    named_by: data.named_by ?? null,
    peak_gust_mph: data.peak_gust_mph ?? null,
    peak_pressure_hpa: data.min_pressure_hpa ?? null,
    rainfall_mm: data.max_rainfall_mm ?? null,
    peak_gust: data.peak_gust ?? null,
    peak_pressure: data.peak_pressure ?? null,
    peak_rainfall: data.peak_rainfall ?? null,
  };
}

/**
 * Update storm statistics (admin-editable, mph / hPa / mm) on a named storm.
 * @param {string} stormId - UUID of the storm row.
 * @param {{ peak_gust?: number|null, peak_pressure?: number|null, peak_rainfall?: number|null }} stats
 * @returns {Promise<void>}
 */
export async function updateStormStats(stormId, stats) {
  const { error } = await supabase
    .from("named_storms")
    .update({
      peak_gust: stats.peak_gust ?? null,
      peak_pressure: stats.peak_pressure ?? null,
      peak_rainfall: stats.peak_rainfall ?? null,
    })
    .eq("id", stormId);
  if (error) throw error;
}

/**
 * Fetch all warnings for a given storm from `storm_warnings`.
 * @param {string} stormId - UUID of the storm.
 * @returns {Promise<Array>}
 */
export async function fetchStormWarnings(stormId) {
  const { data, error } = await supabase
    .from("storm_warnings")
    .select("*")
    .eq("storm_id", stormId)
    .order("created_at", { ascending: true });
  if (error) throw error;
  return data ?? [];
}

/**
 * Insert a new warning into `storm_warnings`.
 * @param {{ storm_id: string, level: string, hazard_type: string, issuing_body: string }} warningData
 * @returns {Promise<Object>} The inserted row.
 */
export async function insertStormWarning(warningData) {
  const { data, error } = await supabase
    .from("storm_warnings")
    .insert([warningData])
    .select()
    .single();
  if (error) throw error;
  return data;
}

/**
 * Delete a warning from `storm_warnings` by id.
 * @param {string} id - UUID of the warning row.
 * @returns {Promise<void>}
 */
export async function deleteStormWarning(id) {
  const { error } = await supabase
    .from("storm_warnings")
    .delete()
    .eq("id", id);
  if (error) throw error;
}

/**
 * Insert a new named storm into `named_storms`.
 * Season is derived automatically from date_named if not provided explicitly.
 * @param {{ storm_name: string, date_named: string, season?: string, notes?: string }} storm
 * @returns {Promise<Object>} The inserted row.
 */
export async function insertNamedStorm({ storm_name, date_named, season, notes = "" }) {
  // Derive season from date_named if not supplied
  if (!season) {
    const d = new Date(date_named);
    const year = d.getFullYear();
    const month = d.getMonth(); // 0-based
    season = month >= 8
      ? `${year}/${String(year + 1).slice(2)}`
      : `${year - 1}/${String(year).slice(2)}`;
  }

  // storm_letter is the first letter of the storm name (NOT NULL in schema)
  const storm_letter = storm_name.trim()[0]?.toUpperCase() ?? "A";

  const { data, error } = await supabase
    .from("named_storms")
    .insert([{ storm_name, storm_letter, season, date_named, notes }])
    .select()
    .single();
  if (error) throw error;
  return data;
}
